import { personName, type QueueItem, type QueueItemStatus, type QueueNode } from '@akpl/shared';
import type { StateCreator } from 'zustand';

import type { TvStore } from './types';

export interface QueueEntry extends QueueItem {
  id: string;
}

export interface QueueState {
  queue: {
    items: Record<string, QueueItem>;
    /** Item ids sorted by `order` (ascending). */
    order: string[];
  };
}

export interface QueueActions {
  /** Replaces the whole queue from the RTDB `queue/` node (WP1.6 `onValue`). */
  setQueue(node: QueueNode | null | undefined): void;
  upsertQueueItem(id: string, item: QueueItem): void;
  removeQueueItem(id: string): void;
}

export type QueueSlice = QueueState & QueueActions;

/**
 * UX-301: the RTDB queue of a session opened before the name fix carries the host's e-mail
 * local part in `addedBy.name`, and the TV prints it on the "Canta:" card for a whole song, on
 * both SIGUIENTE placements and on every drawer row. Migration `0010` cleans the stored rows
 * and the API no longer writes them, but a live session's RTDB node is a snapshot taken when it
 * started — so the TV applies the same rule on the way in. One place, every screen.
 */
function withPersonName(item: QueueItem): QueueItem {
  const name = personName(item.addedBy.name);
  return name === item.addedBy.name ? item : { ...item, addedBy: { ...item.addedBy, name } };
}

function withPersonNames(items: Record<string, QueueItem>): Record<string, QueueItem> {
  const out: Record<string, QueueItem> = {};
  for (const [id, item] of Object.entries(items)) out[id] = withPersonName(item);
  return out;
}

export function sortQueueIds(items: Record<string, QueueItem>): string[] {
  return Object.keys(items).sort((a, b) => {
    const d = items[a]!.order - items[b]!.order;
    return d !== 0 ? d : a < b ? -1 : a > b ? 1 : 0;
  });
}

export const createQueueSlice: StateCreator<TvStore, [], [], QueueSlice> = (set) => ({
  queue: { items: {}, order: [] },
  setQueue: (node) => {
    const items = withPersonNames(node ?? {});
    set({ queue: { items, order: sortQueueIds(items) } });
  },
  upsertQueueItem: (id, item) =>
    set((s) => {
      const items = { ...s.queue.items, [id]: withPersonName(item) };
      return { queue: { items, order: sortQueueIds(items) } };
    }),
  removeQueueItem: (id) =>
    set((s) => {
      if (!(id in s.queue.items)) return {};
      const items = { ...s.queue.items };
      delete items[id];
      return { queue: { items, order: sortQueueIds(items) } };
    }),
});

/** Pure helpers (call inside `useMemo` — they allocate). */
export function queueEntries(state: QueueState['queue']): QueueEntry[] {
  return state.order.map((id) => ({ id, ...state.items[id]! }));
}

export function queueByStatus(state: QueueState['queue'], status: QueueItemStatus): QueueEntry[] {
  return queueEntries(state).filter((e) => e.status === status);
}

/** Rail badge = count of `queued` items only (docs/04 §3). Cheap enough for a selector. */
export const selectQueuedCount = (s: TvStore): number => {
  let n = 0;
  for (const id of s.queue.order) if (s.queue.items[id]?.status === 'queued') n += 1;
  return n;
};

export const selectPlayingId = (s: TvStore): string | null => {
  for (const id of s.queue.order) if (s.queue.items[id]?.status === 'playing') return id;
  return null;
};

/**
 * The song the room is waiting for: the first `queued` item in queue order (UX-191/UX-192).
 * A count alone ("3 en fila") tells nobody whose turn is next, which is the one thing a room
 * driving the TV from its phones needs to read off the screen.
 *
 * It yields the **id**, not the item: a selector that allocates a fresh object on every call
 * makes zustand's snapshot differ every render, which React answers with an infinite loop.
 */
export const selectNextQueuedId = (s: TvStore): string | null => {
  for (const id of s.queue.order) if (s.queue.items[id]?.status === 'queued') return id;
  return null;
};

/** The item the session is on, or null when nothing is playing. Id, for the same reason. */
export const selectNowPlayingId = (s: TvStore): string | null => {
  for (const id of s.queue.order) if (s.queue.items[id]?.status === 'playing') return id;
  return null;
};
